/**
 * ApproachRows — a Harper line's Approach: three settings over its
 * instructions, each `Auto` until a person sets it. They render as rows of
 * the line's blanks card, under the instructions they sit over.
 *
 * Every option says what choosing it changes, right under its control, and
 * `Auto` says what the last run read it as ("Auto · last run: Standard"), so
 * nobody picks a setting blind. The instructions stay the job; a setting a
 * person sets wins over them.
 */

import {
  APPROACH_AUTO_COPY,
  APPROACH_COPY,
  APPROACH_KEYS,
  APPROACH_SETTING_LABEL,
  AUTO_APPROACH,
  approachValueLabel,
  normalizeApproach,
  type ApproachKey,
  type ApproachSources,
  type HarperApproach,
  type ResolvedApproach,
} from "@yagni/shared";

export interface ApproachRowsProps {
  approach: HarperApproach | undefined;
  /** What the last run or preview resolved every setting to. */
  lastResolved: { resolved: ResolvedApproach; sources: ApproachSources } | null | undefined;
  disabled: boolean;
  onChange: (approach: HarperApproach) => void;
  /**
   * Groups by Deploy whatever is set, with this sentence under it. A line that
   * posts on a production deploy reports what shipped, so the runner groups
   * it by deploy and the control says so instead of offering a choice.
   */
  groupByDeployNote?: string | null;
}

/** The line under a setting: the chosen option's meaning, and for Auto what the last run read. */
export function approachHint(
  key: ApproachKey,
  value: string,
  lastResolved: ApproachRowsProps["lastResolved"],
): string {
  if (value !== "auto") {
    return (APPROACH_COPY[key] as Record<string, { meaning: string }>)[value]?.meaning ?? "";
  }
  const last = lastResolved?.resolved[key];
  return last
    ? `${APPROACH_AUTO_COPY.meaning} Last run: ${approachValueLabel(key, last)}.`
    : APPROACH_AUTO_COPY.meaning;
}

export function ApproachRows({ approach, lastResolved, disabled, onChange, groupByDeployNote }: ApproachRowsProps) {
  const current = normalizeApproach(approach ?? AUTO_APPROACH);
  return (
    <>
      {APPROACH_KEYS.map((key) => {
        const options = Object.entries(APPROACH_COPY[key] as Record<string, { label: string }>);
        const locked = key === "groupBy" && Boolean(groupByDeployNote);
        const value = locked ? "deploy" : current[key];
        return (
          <div className="field top" key={key} data-blank={`approach-${key}`}>
            <label>{APPROACH_SETTING_LABEL[key]}</label>
            <div style={{ minWidth: 0 }}>
              <span className="seg" role="radiogroup" aria-label={APPROACH_SETTING_LABEL[key]} data-testid={`approach-${key}`} style={{ flexWrap: "wrap" }}>
                {[["auto", APPROACH_AUTO_COPY.label] as const, ...options.map(([option, copy]) => [option, copy.label] as const)].map(
                  ([option, label]) => (
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      aria-checked={value === option}
                      className={value === option ? "on" : undefined}
                      disabled={disabled || locked}
                      onClick={() => onChange({ ...current, [key]: option } as HarperApproach)}
                    >
                      {label}
                    </button>
                  ),
                )}
              </span>
              <div className="help" style={{ marginTop: 4 }} data-testid={`approach-${key}-hint`}>
                {locked ? groupByDeployNote : approachHint(key, current[key], lastResolved)}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
